import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { API } from './constants';

export interface AuctionImage {
	objectKey: string;
	url: string;
}

export interface Auction {
	_id: string;
	auctionNumber: number;
	title: string;
	description?: string;
	images?: AuctionImage[];
	units?: number;
	category?: string;
	currency: string;
	startingPrice: number;
	minIncrement: number;
	buyNowPrice?: number;
	currentPrice: number;
	currentBidId?: string;
	currentBidderUserId?: string;
	bidCount: number;
	startDate?: string;
	endDate: string;
	actualEndDate: string;
	visibility: 'open' | 'private';
	status: string;
	winnerUserId?: string;
	finalPrice?: number;
	/** True once the winner has acknowledged the victory dialog at least once. */
	victorySeenByWinner?: boolean;
	/** Requesting user's own standing proxy/auto-bid ceiling (0 = none). Only present
	 * on the per-user snapshot from getAuctionById, not on SSE/place-bid payloads. */
	myMaxProxy?: number;
}

export interface AuctionBid {
	_id: string;
	auctionId: string;
	bidderUserId: string;
	/** Anonymous, stable per-bidder label assigned by the backend (e.g. "Usuario 1"). */
	bidderLabel?: string;
	/** Backend flag: true when this bid belongs to the requesting user. */
	isMine?: boolean;
	/** Bank account used — only present on the requester's OWN bids. */
	bidderAccountNumber?: string;
	/** True when placed by the auto-bid engine. */
	isAuto?: boolean;
	amount: number;
	currency: string;
	status: string;
	placedAt?: string;
	createdAt?: string;
}

export interface PlaceBidInput {
	amount: number;
	accountNumber: string;
	/** Optional proxy/auto-bid ceiling. */
	maxProxy?: number;
}

@Injectable({ providedIn: 'root' })
export class AuctionService extends ApiService {
	getAuctions(params: { page?: number; limit?: number; search?: string } = {}) {
		const q = new URLSearchParams();
		q.set('page', String(params.page ?? 1));
		q.set('limit', String(params.limit ?? 50));
		if (params.search) q.set('search', params.search);
		return this.get<{ elements: Auction[]; pagination: any }>(`${API.getAuctions}?${q.toString()}`);
	}

	getAuctionById(id: string) {
		return this.get<Auction>(API.getAuctionById(id));
	}

	getBids(id: string, limit = 50, before?: string) {
		const q = new URLSearchParams({ limit: String(limit) });
		if (before) q.set('before', before);
		return this.get<AuctionBid[]>(`${API.getAuctionBids(id)}?${q.toString()}`);
	}

	/** The winner's pending (unacknowledged) victory, if any. */
	getPendingVictory() {
		return this.get<Auction | null>(API.pendingAuctionVictory);
	}

	/** Marks the victory dialog as seen so it stops auto-opening for this winner. */
	markVictorySeen(id: string) {
		return this.post<{ ok: boolean }>(API.markAuctionVictorySeen(id), { body: {} });
	}

	getMyBids(accountNumber: string, limit = 100) {
		const params = new URLSearchParams({ accountNumber, limit: String(limit) });
		return this.get<AuctionBid[]>(`${API.getMyAuctionBids}?${params.toString()}`);
	}

	placeBid(id: string, input: PlaceBidInput, idempotencyKey?: string) {
		const options: any = { body: input };
		if (idempotencyKey) options.headers = { 'idempotency-key': idempotencyKey };
		return this.post<{ auction: Auction; bid: AuctionBid }>(API.placeAuctionBid(id), options);
	}
}
